import { X, Edit, Lock, Loader2, CheckCircle2, Info, Clock, History, Sparkles } from "lucide-react";
export const handleWorkerUpdated = (
  attendanceId,
  newDepartmentName,
  setWorkerList,
  setCollapsedDepts
) => {
  if (!attendanceId || !newDepartmentName) return;

  setWorkerList((prev) => {
    const updatedWorkers = prev[1].map((w) =>
      w.attendanceId === attendanceId
        ? { ...w, department: newDepartmentName }
        : w
    );
    return [[...prev[0]], [...updatedWorkers]];
  });

  // Expand the new department group
  setCollapsedDepts((prev) => ({
    ...prev,
    [`modal_${newDepartmentName}`]: false,
  }));
};

export const fetchData = async ({
  setLoading, setError, coords, setCompanies, setSelectedCompany,
  fetchNearestCompany
}) => {
  try {
    setLoading(true);
    setError(null);

    const companyRes = await fetchNearestCompany({
      latitude: coords.latitude,
      longitude: coords.longitude,
    });

    if (companyRes && companyRes.status && companyRes.companyId) {
      setCompanies([companyRes]);
      setSelectedCompany(companyRes.companyId);
    } else {
      throw new Error(
        companyRes?.msg || companyRes?.error || "No nearby company found"
      );
    }
  } catch (err) {
    setError(
      err.response?.data?.message ||
      err.response?.data?.error ||
      err.message ||
      "Failed to load companies"
    );
  } finally {
    setLoading(false);
  }
};

export const fetchDepartments = async ({
  setLoadingDepartments, setDepartmentsError, selectedCompany, setDepartments, setDepartmentCounts,fetchDepartmentsBySkills
}) => {
  try {
    setLoadingDepartments(true);
    setDepartmentsError(null);

    const deptRes = await fetchDepartmentsBySkills(selectedCompany);

    if (deptRes && Array.isArray(deptRes.departments)) {
      const list = deptRes.departments.map((d) => ({
        _id: d._id,
        name: d.department || d.name,
      }));
      setDepartments(list);
      setDepartmentCounts(list.reduce((acc, d) => ({ ...acc, [d._id]: "" }), {}));
    } else {
      throw new Error(
        deptRes?.msg || deptRes?.error || "Invalid departments data received"
      );
    }
  } catch (err) {
    setDepartmentsError(
      err.response?.data?.message ||
      err.response?.data?.error ||
      err.message ||
      "Failed to load departments"
    );
    setDepartments([]);
  } finally {
    setLoadingDepartments(false);
  }
};

export const handleAssign = async ({
  selectedCompany, allocatedTotal, notfreezedworker, setAssignError, setShowErrorModal,
  setAssigning, departments, departmentCounts, setDepartmentCounts, onAssign,
  setAssignResult, setShowSuccessModal, fetchWorkers,assignWorkers
}) => {
  if (!selectedCompany) return;

  if (allocatedTotal > notfreezedworker) {
    setAssignError("Total assigned worker is greater than total workers");
    return;
  }


  try {
    setAssigning(true);
    setAssignError(null);

    const departmentsPayload = departments
      .map((d) => ({
        departmentId: d._id,
        count: parseInt(departmentCounts[d._id], 10) || 0,
      }))
      .filter((a) => a.count > 0);

    const payload = {
      companyId: selectedCompany,
      departments: departmentsPayload,
    };

    const assignRes = await assignWorkers(payload);

    if (assignRes && assignRes.status === false) {
      throw new Error(
        assignRes?.message || assignRes?.error || "Failed to assign departments"
      );
    }

    // Assignment succeeded — reset counts and refresh the worker list
    setDepartmentCounts(
      departments.reduce((acc, d) => ({ ...acc, [d._id]: "" }), {})
    );
    setShowErrorModal(false);
    setAssignResult(assignRes);
    setShowSuccessModal(true);

    // refresh worker summary so totals reflect the new assignment
    await fetchWorkers(selectedCompany);

    if (onAssign) await onAssign(assignRes);
  } catch (err) {
    const message =
      err.response?.data?.message ||
      err.response?.data?.error ||
      err.message ||
      "Failed to assign departments";
    setAssignError(message);
    setShowErrorModal(true);
  } finally {
    setAssigning(false);
  }
};

export const ConfirmModal = ({
  isOpen, onClose, onConfirm,
  title, description,
  confirmLabel = 'Confirm',
  confirmClass = 'bg-indigo-600 hover:bg-indigo-700',
  cancelLabel = 'Cancel',
  loading = false,
  icon, iconBg = 'bg-indigo-50',
}) => {
  if (!isOpen) return null;
  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-[320px] lg:w-[360px] p-6 lg:p-7 flex flex-col items-center gap-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`w-14 h-14 rounded-full flex items-center justify-center ${iconBg}`}>
          {icon}
        </div>
        <h2 className="text-sm lg:text-base font-bold text-gray-800 text-center">{title}</h2>
        <p className="text-xs lg:text-sm text-gray-500 text-center leading-relaxed">{description}</p>
        <div className="flex gap-3 w-full mt-1">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 py-1.5 lg:py-2 text-xs lg:text-sm border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50 font-medium transition-colors disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`flex-1 py-1.5 lg:py-2 text-xs lg:text-sm text-white rounded-md font-medium transition-colors flex items-center justify-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed ${confirmClass}`}
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export const FreezeResultModal = ({ isOpen, onClose, result }) => {
  if (!isOpen || !result) return null;

  const isFailure = (result.freezedCount === 0) && result.skippedCount > 0;

  return (
    <div
      className="fixed inset-0 z-[400] flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-[300px] lg:w-[340px] p-6 lg:p-7 flex flex-col items-center gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`w-14 h-14 rounded-full flex items-center justify-center ${isFailure ? "bg-red-50" : "bg-emerald-50"}`}>
          {isFailure
            ? <X className="w-7 h-7 text-red-500" />
            : <CheckCircle2 className="w-7 h-7 text-emerald-500" />
          }
        </div>
        <div className="text-center">
          <h2 className="text-sm lg:text-base font-bold text-gray-800 mb-1">
            {isFailure ? "Allotment Failed" : "Allotment Completed"}
          </h2>
          <p className="text-xs lg:text-sm text-gray-500">{result.message}</p>
        </div>

        <div className="w-full flex flex-col gap-2">
          {!isFailure && (
            <div className="flex items-center justify-between px-3 py-2 bg-emerald-50 rounded-lg">
              <span className="text-xs lg:text-sm text-emerald-700 font-medium">Successfully Allotted</span>
              <span className="text-sm lg:text-base font-bold text-emerald-800">{result.allottedCount}</span>
            </div>
          )}

          {result.skippedCount > 0 && (
            <>
              <div className={`flex items-center justify-between px-3 py-2 rounded-lg ${isFailure ? "bg-red-50" : "bg-amber-50"}`}>
                <span className={`text-xs lg:text-sm font-medium ${isFailure ? "text-red-700" : "text-amber-700"}`}>
                  {isFailure ? "Failed / Skipped" : "Skipped"}
                </span>
                <span className={`text-sm lg:text-base font-bold ${isFailure ? "text-red-800" : "text-amber-800"}`}>
                  {result.skippedCount}
                </span>
              </div>
              <p className={`mt-1 px-3 text-xs ${isFailure ? "text-red-600" : "text-amber-600"}`}>
                Some changes were made by another supervisor. Please refresh the page.
              </p>
            </>
          )}
        </div>

        <button
          onClick={onClose}
          className={`w-full py-2 lg:py-2.5 text-xs lg:text-sm font-semibold text-white rounded-lg transition-colors ${
            isFailure ? "bg-red-600 hover:bg-red-700" : "bg-indigo-600 hover:bg-indigo-700"
          }`}
        >
          Done
        </button>
      </div>
    </div>
  );
};

export const ALLOCATION_REASON_CONFIG = {
  recency: {
    label: "Recent Activity",
    description: "This worker was allocated based on how recently they've worked shifts in this department.",
    icon: History,
    bg: "bg-blue-50",
    text: "text-blue-700",
    iconColor: "#2563eb",
  },
  department_history: {
    label: "Department History",
    description: "This worker was allocated based on their overall shift history in this department.",
    icon: History,
    bg: "bg-blue-50",
    text: "text-blue-700",
    iconColor: "#2563eb",
  },
  skill_match: {
    label: "Skill Match",
    description: "This worker was allocated based on skills matching this department's requirements.",
    icon: History,
    bg: "bg-blue-50",
    text: "text-blue-700",
    iconColor: "#2563eb",
  },
  master_department_history: {
    label: "Master Department History",
    description: "This worker was allocated based on Master Department.",
    icon: History,
    bg: "bg-blue-50",
    text: "text-blue-700",
    iconColor: "#2563eb",
  },
  master_sub_department_history: {
    label: "Master SubDepartment History",
    description: "This worker was allocated based on Master SubDepartment.",
    icon: History,
    bg: "bg-blue-50",
    text: "text-blue-700",
    iconColor: "#2563eb",
  },
};

export const getAllocationConfig = (reason) =>
  ALLOCATION_REASON_CONFIG[reason] || {
    label: reason || "Not Alloted",
    icon: Info,
    bg: "bg-gray-50",
    text: "text-gray-700",
    iconColor: "#6b7280",
  };

export const AllocationInfoModal = ({ isOpen, onClose, worker }) => {
  if (!isOpen || !worker) return null;

  const meta = worker.allocationMeta || {};
  const config = getAllocationConfig(meta.reason);
  const ReasonIcon = config.icon;

  const showDeptShifts = meta.reason === "recency" || meta.reason === "department_history";
  const showSkillMatch = meta.reason === "skill_match";

  return (
    <div
      className="fixed inset-0 z-[500] flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-[320px] lg:w-[380px] p-6 lg:p-7 flex flex-col gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-sm lg:text-base font-bold text-gray-800">Allocation Details</h2>

          <button
            onClick={onClose}
            className="flex items-center justify-center w-6 h-6 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-2 space-y-1 text-sm lg:text-base text-gray-700">
          <p><span className="font-semibold">Name:</span> {worker?.name}</p>
          <p><span className="font-semibold">Phone No:</span> {worker?.phone}</p>
          {worker?.workerCode && (
            <p><span className="font-semibold">Worker Code:</span> {worker.workerCode}</p>
          )}
        </div>

        {/* Reason banner */}
        <div className={`flex items-start gap-2.5 px-3 py-2.5 rounded-lg ${config.bg}`}>
          <ReasonIcon size={16} color={config.iconColor} className="flex-shrink-0 mt-0.5" />
          <div>
            <p className={`text-xs lg:text-sm font-semibold ${config.text}`}>{config.label}</p>
            <p className="text-[11px] lg:text-xs text-gray-500 mt-0.5 leading-relaxed">
              {config.description}
            </p>
          </div>
        </div>

        {/* Metrics */}
        <div className="flex flex-col gap-2">
          {showDeptShifts && (
            <div className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg">
              <span className="text-xs lg:text-sm text-gray-600">Total shifts in {worker.department}</span>
              <span className="text-sm lg:text-base font-bold text-gray-900">
                {meta.totalShiftsInDept ?? 0}
              </span>
            </div>
          )}
        </div>

        <button
          onClick={onClose}
          className="w-full py-2 lg:py-2.5 text-xs lg:text-sm font-semibold text-white rounded-lg bg-indigo-600 hover:bg-indigo-700 transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  );
};

export const FreezeIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
    stroke="#d97706" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

export const handleFreezeConfirmed = async ({
  company,
  workers,
  freezeConfirm,
  setFreezeConfirm,
  closeFreezeConfirm,
  onRefetchWorkers,
  setFreezeResult,
  freezeDepartmentApi
}) => {
  setFreezeConfirm((prev) => ({ ...prev, loading: true }));
  try {
    const companyId = company?.companyId;
    if (!companyId) {
      console.error("company or companyId is missing:", company);
      closeFreezeConfirm();
      return;
    }

    let records;
    if (freezeConfirm.type === 'all') {
      records = workers
        .filter((w) => !w.isDepartmentFreezed && (w.department || "Not Assigned") !== "Not Assigned")
        .map((w) => ({
          attendanceId: w.attendanceId,
          departmentId: w.departmentId || null,
        }));
    } else {
      records = workers
        .filter(
          (w) => (w.department || "Not Assigned") === freezeConfirm.deptName
            && !w.isDepartmentFreezed
        )
        .map((w) => ({
          attendanceId: w.attendanceId,
          departmentId: w.departmentId || null,
        }));
    }

    const freezeRes = await freezeDepartmentApi({ companyId, records });

    if (onRefetchWorkers) await onRefetchWorkers();

    closeFreezeConfirm();

    if (freezeRes) {
      setFreezeResult({ open: true, data: freezeRes });
    }
  } catch (err) {
    console.error(err);
    closeFreezeConfirm();
  }
};

export const handleUpdateDepartment = async ({
  editingWorker,
  selectedDepartmentId,
  setUpdating,
  setUpdateError,
  departments,
  setEditingWorker,
  setCollapsedDepts,
  onWorkerUpdated,
  onRefetchWorkers,
  seteditmodal,
  updateWorkerDepartmentApi
}) => {
  if (!editingWorker?.attendanceId || !selectedDepartmentId) return;
  try {
    setUpdating(true);
    setUpdateError(null);

    const prevDepartmentId = editingWorker.departmentId || null;

    const res = await updateWorkerDepartmentApi({
      attendanceId: editingWorker.attendanceId,
      departmentId: selectedDepartmentId,
      prevDepartmentId,
    });

    if (res?.status) {
      const updatedDept = departments.find((d) => d._id === selectedDepartmentId);
      const newDeptName = updatedDept?.name || "";

      setEditingWorker((prev) => ({
        ...prev,
        department: newDeptName,
        departmentId: selectedDepartmentId,
      }));

      setCollapsedDepts((prev) => ({ ...prev, [`modal_${newDeptName}`]: false }));

      if (onWorkerUpdated) onWorkerUpdated(editingWorker.attendanceId, newDeptName);
      if (onRefetchWorkers) await onRefetchWorkers();

      seteditmodal(false);
      setEditingWorker(null);
    } else {
      throw new Error(res?.message || res?.error || "Failed to update department");
    }
  } catch (err) {
    setUpdateError(
      err.response?.data?.message ||
      err.response?.data?.error ||
      err.message ||
      "Failed to update department"
    );
  } finally {
    setUpdating(false);
  }
};